import { useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Loader from '../../../../../ts/components/loader/Loader';
import { usePageEditor } from '../../../../../ts/components/page-editor/components/PageEditorProvider';
import ComponentSuggest from '../../../../../ts/components/page-editor/components/gui/componentSuggest/ComponentSuggest';
import Metadata from '../../../../../ts/components/page-editor/components/gui/toolbar/Metadata';
import Toolbar from '../../../../../ts/components/page-editor/components/gui/toolbar/Toolbar';
import translations from '../../../../../ts/translations';
import { useDocumentKeyDown } from '../../../../hooks/document';
import type { Tab } from '../../../../types/tab';
import Composer from './composer/Composer';
import ConfigPanel from './composer/config-panel/ConfigPanel';

const Main = ({ tab, isActive }: { tab: Tab; isActive: boolean }) => {
    const { state, setPageTab, onSave, isLoading, setShowMetadataEditor } = usePageEditor();

    const { page, showMetadataEditor, showComponentSuggest } = state;
    const { showCloseConfirmation, elementId } = tab;

    const mainContainerRef = useRef(null);

    // Save a copy of the tab for the same reason, so we can get our hands on the key and elementId
    // without going to Redux or passing the tab config down several layers deep.
    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        if (tab) {
            setPageTab(tab);
        }
    }, [tab]);

    const handleKeyDown = () => {
        // Save the currently active page when there are changes and user triggers the CTRL+S hotkey.
        page.id === elementId && isActive && showCloseConfirmation && onSave(page);
    };

    useDocumentKeyDown('CTRL+S', mainContainerRef.current, handleKeyDown);

    if (isLoading) {
        return <Loader message={translations.PAGE_BUILDER_loading_page} />;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <main ref={mainContainerRef} className="main-wrapper" data-testid="main-wrapper">
                <Toolbar />
                <Composer isActive={isActive} />
                <ConfigPanel ref={mainContainerRef} />
                {showMetadataEditor && page.id && (
                    <Metadata showModal={setShowMetadataEditor} pageId={page.id} />
                )}
                {showComponentSuggest && <ComponentSuggest />}
            </main>
        </DndProvider>
    );
};

export default Main;
